import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import type { z } from 'zod';

import {
  CreateOrganizationTemplateRequestSchema,
  OrganizationTemplateDetailModel,
  OrganizationTemplatesModel,
  OrganizationTemplatesWithPaginationModel,
  UpdateOrganizationTemplateRequestSchema,
} from '../_models/organization-template';
import {
  organizationTemplateInvalidateQueryKeyMap,
  organizationTemplateQueryKeyMap,
} from '../_utils/query-keys';

const API_BASE_URL = '/api/organizations/templates/';

/**
 * 所属組織が利用組織に設定されているテンプレートのみを取得する
 *
 * ページネーションなし。事業作成/編集画面でのテンプレート選択に使用
 */
export const useOrganizationTemplatesByJoinOrganization = <
  T = z.infer<typeof OrganizationTemplatesModel>,
>(
  select?: (data: z.infer<typeof OrganizationTemplatesModel>) => T,
) => {
  return useQuery({
    queryKey: organizationTemplateQueryKeyMap.templates(),
    queryFn: async () => {
      const res = await axiosGet(API_BASE_URL);
      return OrganizationTemplatesModel.parse(res);
    },
    select,
  });
};

/**
 * 組織テンプレート一覧を取得（ページネーション付き）
 *
 * テンプレート管理画面用。利用組織、テンプレート組織の情報も含む
 */
export const useOrganizationTemplatesWithPagination = <
  T = z.infer<typeof OrganizationTemplatesWithPaginationModel>,
>(
  params: {
    page: number;
    pageSize: number;
    cascader?: string | null;
    q?: string | null;
  },
  select?: (
    data: z.infer<typeof OrganizationTemplatesWithPaginationModel>,
  ) => T,
) => {
  return useQuery({
    queryKey: organizationTemplateQueryKeyMap.templatesWithPagination(params),
    queryFn: async () => {
      const res = await axiosGet(
        `${API_BASE_URL}list-with-pagination/`,
        params,
      );
      return OrganizationTemplatesWithPaginationModel.parse(res);
    },
    select,
  });
};

/**
 * 組織テンプレート詳細を取得
 */
export const useOrganizationTemplate = (id: string) => {
  return useQuery({
    queryKey: organizationTemplateQueryKeyMap.template(id),
    queryFn: async () => {
      const res = await axiosGet(`${API_BASE_URL}${id}/`);
      return OrganizationTemplateDetailModel.parse(res);
    },
    enabled: !!id,
  });
};

/**
 * 組織テンプレートを作成
 */
export const useCreateOrganizationTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof CreateOrganizationTemplateRequestSchema>,
    ) => {
      const data = CreateOrganizationTemplateRequestSchema.parse(values);
      return await axiosPost(API_BASE_URL, data);
    },
    onSuccess: async () => {
      await Promise.all(
        organizationTemplateInvalidateQueryKeyMap
          .updateTemplateList()
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
    },
  });
};

/**
 * 組織テンプレートを更新
 */
export const useUpdateOrganizationTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateOrganizationTemplateRequestSchema>,
    ) => {
      const { id, ...data } =
        UpdateOrganizationTemplateRequestSchema.parse(values);
      return await axiosPatch(`${API_BASE_URL}${id}/`, data);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all(
        organizationTemplateInvalidateQueryKeyMap
          .updateTemplateList(id)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
    },
  });
};

/**
 * 組織テンプレートを削除
 */
export const useDeleteOrganizationTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();

  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`${API_BASE_URL}${id}/`);
    },
    onSuccess: async (_, id) => {
      await Promise.all(
        organizationTemplateInvalidateQueryKeyMap
          .updateTemplateList(id)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      removeQuery(organizationTemplateQueryKeyMap.template(id));
    },
  });
};
